import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import type { LtlBookingConfirmation } from '@shipstation/client-common/types/ltl';
import type { LtlFreightBookingSource } from '../../pages/ltl-freight/tracking-events';

import { BookingConfirmationView } from '../../pages/ltl-freight/components/freight-booking-wizard/components/booking-confirmation/booking-confirmation-view';
import { DeferBuyNowModal } from '../../components/defer-buy-now-modal/defer-buy-now-modal';
import { DdpTermsAndConditionsModal } from '../../components/modals/ddp-terms-and-conditions-modal/ddp-terms-and-conditions-modal';
import { PhoneNumberCaptureModal } from '../../components/phone-number-capture-modal/phone-number-capture-modal';
import { TrialUpsellModal } from '../../components/upsell-modal/trial-upsell-modal/trial-upsell-modal';
import { BannerSources, TrialTiers } from '../../components/plan-features-trial-upsell-banner/constants';
import { emptyObject } from 'ui-components/default-props/empty';
import { PaymentUpdateModalWrapper as PaymentUpdateModalWrapperBase } from '../../financial-services-ui/payment-update/index';
import { useModal } from '../../hooks/use-modal';
import { NewLineupPromotionalMigrationLoader } from '../../pages/settings/payment-and-subscription/components/migrate-plan-modal/new-lineup-promotional-migration-loader';
import { useGetEmailMarketingHandoffModalDetails } from '../../pages/settings/payment-and-subscription/components/migrate-plan-modal/use-get-email-marketing-handoff-modal-details';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';
import { ManageSubscriptionDrawerContextProvider } from '../../pages/settings/payment-and-subscription/context/manage-subscription-drawer-context';
import { WalletRegistrationModal } from '../../wallet/modals/wallet-registration';
import { WalletUpdateModal } from '../../wallet/modals/wallet-update';
import { PAYMENT_TAB } from '../../wallet/utilities/constants';
import { Segment } from '../../services/analytics-services/segment';
import { PHONE_CAPTURE_MODAL_OPENED } from '../../constants/analytics-events';
import type { AuctanePaySession } from '../../financial-services-ui/types/fsui';
import type { TransactionCategory, StoredPaymentMethod } from '@shipstation/financial-services-ui-components';
import type { PhoneCaptureSource } from '../../components/phone-number-capture/constants';

export type WalletRegistrationModalProps = {
  walletId?: string;
  postWalletRegistrationSuccessPayload?: Array<{ fulfillmentPlanId: string; shipFromId: string }>;
  labelPurchaseCallback?: () => void;
  selectedFulfillmentPlanIds?: string[];
  sellerLabelProviderIds?: string[];
  initialFormValues?: Record<string, unknown>;
  preloadedSession?: AuctanePaySession;
  onClose?: () => void;
  totalCost?: number;
};

export type WalletUpdateModalProps = {
  walletId?: string;
  labelPurchaseCallback?: () => void;
};

export type BillingInfoModalProps = {
  transactionCategory?: TransactionCategory;
  trackConfirmClick?: () => void;
  onClose?: () => void;
};

export type ManageSubscriptionDrawerProps = {
  initialIsManageModalOpen?: boolean;
  onClose?: () => void;
  onInterceptClose?: () => boolean;
  shouldShowTrialAutoConvertedToFreeBanner?: boolean;
  desiredShipmentLimit?: number;
  desiredNumberOfAdditionalUsers?: number;
  selectedPlanId?: string;
  addOnsToAdd?: Array<Record<string, unknown>>;
  addOnsToRemove?: Array<Record<string, unknown>>;
  newPaymentMethod?: StoredPaymentMethod[];
  upsellWorkflowGuid?: string;
  highlightSection?: string;
};

export type BookingConfirmationModalProps = {
  freightShipmentId: string;
  bookingConfirmation: LtlBookingConfirmation | null;
  source?: LtlFreightBookingSource;
  isLoading?: boolean;
};

export type GlobalModalLaunchContextValue = {
  currentWalletRegistrationStepId: string;
  setCurrentWalletRegistrationStepId: (stepId: string) => void;
  openWalletRegistration: (props?: WalletRegistrationModalProps, initialStepId?: string) => void;
  closeWalletRegistration: () => void;
  openWalletUpdate: (props?: WalletUpdateModalProps) => void;
  closeWalletUpdate: () => void;
  openBillingInfoModal: (props?: BillingInfoModalProps) => void;
  closeBillingInfoModal: () => void;
  isBillingInfoModalOpen: boolean;
  openManageSubscriptionDrawer: (props?: ManageSubscriptionDrawerProps) => void;
  closeManageSubscriptionDrawer: () => void;
  openDeferBuyNowModal: () => void;
  closeDeferBuyNowModal: () => void;
  openDdpTermsModal: () => void;
  closeDdpTermsModal: () => void;
  registerDdpTermsModalClosedCallback: (callback: () => void) => void;
  openPhoneCaptureModal: (source?: PhoneCaptureSource) => void;
  closePhoneCaptureModal: () => void;
  openPremiumTrialModal: () => void;
  closePremiumTrialModal: () => void;
  openBookingConfirmation: (props: BookingConfirmationModalProps) => void;
  closeBookingConfirmation: () => void;
};

const PaymentUpdateModalWrapper = PaymentUpdateModalWrapperBase as FC<BillingInfoModalProps & { onClose: () => void }>;

export const GlobalModalLaunchContext = createContext<GlobalModalLaunchContextValue | null>(null);
export const useGlobalModalLaunchContext = () => useContext(GlobalModalLaunchContext);

export const GlobalModalLaunchContextProvider = ({ children }: { children: ReactNode }) => {
  // TODO: properly type when mega context is converted to TS
  const { setSelectedPlanId, setDesiredNumberOfAdditionalUsers } = usePaymentAndSubscriptionMegaContext() as unknown as {
    setSelectedPlanId: (updater: (prev: string | undefined) => string | undefined) => void;
    setDesiredNumberOfAdditionalUsers: (updater: (prev: number | undefined) => number | undefined) => void;
  };

  // WalletRegistrationModal
  const [currentWalletRegistrationStepId, setCurrentWalletRegistrationStepId] = useState(PAYMENT_TAB);
  const [walletRegistrationProps, setWalletRegistrationProps] = useState<WalletRegistrationModalProps | undefined>();
  const { close: closeWalletRegistration, isOpen: isWalletRegistrationOpen, open: openWalletRegistration } = useModal();
  const handleOpenWalletRegistration = useCallback(
    (newWalletRegistrationProps?: WalletRegistrationModalProps, initialStepId?: string) => {
      if (initialStepId) {
        // allows for setting of initial step id, and then takes ownership of anything further
        setCurrentWalletRegistrationStepId(initialStepId);
      }
      setWalletRegistrationProps(newWalletRegistrationProps);
      openWalletRegistration();
    },
    [openWalletRegistration],
  );
  const handleCloseWalletRegistration = useCallback(() => {
    // possibly allow other callbacks to pass in here, unsure yet of pattern
    setWalletRegistrationProps(undefined);
    setCurrentWalletRegistrationStepId(PAYMENT_TAB);
    closeWalletRegistration();
  }, [closeWalletRegistration, setWalletRegistrationProps]);

  // WalletUpdateModal
  const [walletUpdateProps, setWalletUpdateProps] = useState<WalletUpdateModalProps | undefined>();
  const { close: closeWalletUpdateModal, isOpen: isWalletUpdateModalOpen, open: openWalletUpdateModal } = useModal();
  const handleOpenWalletUpdateModal = useCallback(
    (newWalletUpdateProps?: WalletUpdateModalProps) => {
      setWalletUpdateProps(newWalletUpdateProps);
      openWalletUpdateModal();
    },
    [openWalletUpdateModal, setWalletUpdateProps],
  );
  const handleCloseWalletUpdateModal = useCallback(() => {
    // possibly allow other callbacks to pass in here, unsure yet of pattern
    setWalletUpdateProps(undefined);
    closeWalletUpdateModal();
  }, [closeWalletUpdateModal, setWalletUpdateProps]);

  // BillingInfoModal
  const [billingInfoModalProps, setBillingInfoModalProps] = useState<BillingInfoModalProps | undefined>();
  const [isBillingInfoModalOpen, setIsBillingInfoModalOpen] = useState(false);
  const handleOpenBillingInfoModal = useCallback(
    (props?: BillingInfoModalProps) => {
      setBillingInfoModalProps(props);
      setIsBillingInfoModalOpen(true);
    },
    [setIsBillingInfoModalOpen],
  );
  const handleCloseBillingInfoModal = useCallback(() => {
    setBillingInfoModalProps(undefined);
    setIsBillingInfoModalOpen(false);
  }, [setIsBillingInfoModalOpen]);

  // ManageSubscriptionDrawer
  const [manageSubscriptionDrawerProps, setManageSubscriptionDrawerProps] = useState<ManageSubscriptionDrawerProps>(emptyObject);
  const { close: closeManageSubscriptionDrawer, isOpen: isManageSubscriptionDrawerOpen, open: openManageSubscriptionDrawer } = useModal();
  const handleOpenManageSubscriptionDrawer = useCallback(
    (props: ManageSubscriptionDrawerProps = emptyObject) => {
      setSelectedPlanId((prev) => props?.selectedPlanId ?? prev);
      setDesiredNumberOfAdditionalUsers((prev) => props?.desiredNumberOfAdditionalUsers ?? prev);
      setManageSubscriptionDrawerProps(props);

      setTimeout(() => {
        // ensure that the drawer is open after the props are set
        openManageSubscriptionDrawer();
      }, 0);
    },
    [openManageSubscriptionDrawer, setDesiredNumberOfAdditionalUsers, setSelectedPlanId],
  );
  const handleCloseManageSubscriptionDrawer = useCallback(() => {
    setManageSubscriptionDrawerProps(emptyObject);
    closeManageSubscriptionDrawer();
  }, [closeManageSubscriptionDrawer, setManageSubscriptionDrawerProps]);

  // DeferBuyNowModal
  const { close: closeDeferBuyNowModal, isOpen: isDeferBuyNowModalOpen, open: openDeferBuyNowModal } = useModal();

  // DdpTermsAndConditionsModal (for direct access to terms)
  const { close: closeDdpTermsModal, isOpen: isDdpTermsModalOpen, open: openDdpTermsModal } = useModal();
  const ddpTermsModalClosedCallbackRef = useRef<(() => void) | undefined>();

  const handleCloseDdpTermsModal = useCallback(() => {
    if (ddpTermsModalClosedCallbackRef.current) {
      ddpTermsModalClosedCallbackRef.current();
    }
    closeDdpTermsModal();
  }, [closeDdpTermsModal]);

  const registerDdpTermsModalClosedCallback = useCallback((callback: () => void) => {
    ddpTermsModalClosedCallbackRef.current = callback;
  }, []);

  // PhoneNumberCaptureModal
  const [phoneCaptureModalProps, setPhoneCaptureModalProps] = useState<{ source?: PhoneCaptureSource } | undefined>();
  const { close: closePhoneCaptureModal, isOpen: isPhoneCaptureModalOpen, open: openPhoneCaptureModal } = useModal();
  const handleOpenPhoneCaptureModal = useCallback(
    (source?: PhoneCaptureSource) => {
      setPhoneCaptureModalProps({ source });
      Segment.trackEvent(PHONE_CAPTURE_MODAL_OPENED, { source });
      openPhoneCaptureModal();
    },
    [openPhoneCaptureModal],
  );
  const handleClosePhoneCaptureModal = useCallback(() => {
    setPhoneCaptureModalProps(undefined);
    closePhoneCaptureModal();
  }, [closePhoneCaptureModal]);

  // PremiumTrialModal (opened from the billing migration Try Premium todo card)
  const { close: closePremiumTrialModal, isOpen: isPremiumTrialModalOpen, open: openPremiumTrialModal } = useModal();

  const [bookingConfirmationProps, setBookingConfirmationProps] = useState<BookingConfirmationModalProps | undefined>();
  const { close: closeBookingConfirmationModal, isOpen: isBookingConfirmationOpen, open: openBookingConfirmationModal } = useModal();
  const handleOpenBookingConfirmation = useCallback(
    (props: BookingConfirmationModalProps) => {
      setBookingConfirmationProps(props);
      openBookingConfirmationModal();
    },
    [openBookingConfirmationModal],
  );
  const handleCloseBookingConfirmation = useCallback(() => {
    setBookingConfirmationProps(undefined);
    closeBookingConfirmationModal();
  }, [closeBookingConfirmationModal]);

  const { shouldShowMarketingHandoffModal, hideMarketingHandoffModal } = useGetEmailMarketingHandoffModalDetails();

  // memo value
  const value = useMemo(
    () => ({
      currentWalletRegistrationStepId,
      setCurrentWalletRegistrationStepId,
      openWalletRegistration: handleOpenWalletRegistration,
      closeWalletRegistration: handleCloseWalletRegistration,
      openWalletUpdate: handleOpenWalletUpdateModal,
      closeWalletUpdate: handleCloseWalletUpdateModal,
      openBillingInfoModal: handleOpenBillingInfoModal,
      closeBillingInfoModal: handleCloseBillingInfoModal,
      openManageSubscriptionDrawer: handleOpenManageSubscriptionDrawer,
      closeManageSubscriptionDrawer: handleCloseManageSubscriptionDrawer,
      openDeferBuyNowModal,
      closeDeferBuyNowModal,
      openDdpTermsModal,
      closeDdpTermsModal: handleCloseDdpTermsModal,
      registerDdpTermsModalClosedCallback,
      openPhoneCaptureModal: handleOpenPhoneCaptureModal,
      closePhoneCaptureModal: handleClosePhoneCaptureModal,
      openPremiumTrialModal,
      closePremiumTrialModal,
      openBookingConfirmation: handleOpenBookingConfirmation,
      closeBookingConfirmation: handleCloseBookingConfirmation,

      // rather than render here, allow this to be used in the consuming component on the specific redirect page
      // so that the component is guaranteed to be within the right context(s)
      isBillingInfoModalOpen,
    }),
    [
      currentWalletRegistrationStepId,
      handleOpenWalletRegistration,
      handleCloseWalletRegistration,
      handleOpenWalletUpdateModal,
      handleCloseWalletUpdateModal,
      handleOpenBillingInfoModal,
      handleCloseBillingInfoModal,
      handleOpenManageSubscriptionDrawer,
      handleCloseManageSubscriptionDrawer,
      openDeferBuyNowModal,
      closeDeferBuyNowModal,
      openDdpTermsModal,
      handleCloseDdpTermsModal,
      registerDdpTermsModalClosedCallback,
      handleOpenPhoneCaptureModal,
      handleClosePhoneCaptureModal,
      openPremiumTrialModal,
      closePremiumTrialModal,
      handleOpenBookingConfirmation,
      handleCloseBookingConfirmation,
      isBillingInfoModalOpen,
    ],
  );

  return (
    <GlobalModalLaunchContext.Provider value={value}>
      {children}
      {isWalletRegistrationOpen && walletRegistrationProps ? <WalletRegistrationModal {...walletRegistrationProps} /> : null}
      {isWalletUpdateModalOpen && walletUpdateProps ? <WalletUpdateModal {...walletUpdateProps} /> : null}
      {isBillingInfoModalOpen ? <PaymentUpdateModalWrapper onClose={handleCloseBillingInfoModal} {...billingInfoModalProps} /> : null}
      {isManageSubscriptionDrawerOpen ? (
        <ManageSubscriptionDrawerContextProvider
          initialIsManageModalOpen
          onClose={handleCloseManageSubscriptionDrawer}
          {...manageSubscriptionDrawerProps}
        />
      ) : null}
      {isDeferBuyNowModalOpen ? <DeferBuyNowModal onClose={closeDeferBuyNowModal} /> : null}
      {isDdpTermsModalOpen ? <DdpTermsAndConditionsModal onClose={handleCloseDdpTermsModal} /> : null}
      {isPhoneCaptureModalOpen && phoneCaptureModalProps ? (
        <PhoneNumberCaptureModal onClose={handleClosePhoneCaptureModal} {...phoneCaptureModalProps} />
      ) : null}
      {shouldShowMarketingHandoffModal && (
        <NewLineupPromotionalMigrationLoader hideMarketingHandoffModal={hideMarketingHandoffModal} shouldStartWithEmailHandoff />
      )}
      {isPremiumTrialModalOpen && (
        <TrialUpsellModal onClose={closePremiumTrialModal} source={BannerSources.Todo} tier={TrialTiers.Premium} />
      )}
      {isBookingConfirmationOpen && bookingConfirmationProps ? (
        <BookingConfirmationView
          {...bookingConfirmationProps}
          isLoading={bookingConfirmationProps.isLoading ?? false}
          onClose={handleCloseBookingConfirmation}
        />
      ) : null}
    </GlobalModalLaunchContext.Provider>
  );
};
